import React, { useReducer } from 'react'
import { Checkbox, CheckboxGroup } from '@hub/checkbox'
import { Grid, GridItem } from '@hub/grid'
import { SearchMediumIcon } from '@hub/icon'
import { Input, InputGroup, InputLeftElement } from '@hub/input'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import { sortBy, debounce } from 'lodash'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { CheckboxWithLink } from './checkbox-with-link'

export type FilterGroup = {
  label?: string
  options?: {
    label: string
    value: string
  }[]
}

export type OnSearchFunc = (
  id: string,
  unfilteredOptionGroups: FilterGroup[],
  searchTerm: string
) => Promise<FilterGroup[]>

type OnChangeFunc = (
  selectedOptions: string[],
  { option }: { option: string; isChecked: boolean }
) => void

interface SearchableFacetedFilterProps {
  id: string
  title: string
  isSearchable?: boolean
  isLinked?: boolean
  searchFieldPlaceholder?: string
  optionGroups?: FilterGroup[]
  selectedOptions?: string[]
  onChange: OnChangeFunc
  onSearch?: OnSearchFunc
}

export async function _filterOptonsBySearchTerm(
  _id: string,
  unfilteredOptionGroups: FilterGroup[],
  search: string
): Promise<FilterGroup[]> {
  return unfilteredOptionGroups
    .map(group => {
      const options = group.options?.filter(option =>
        option.label.toLowerCase().includes(search.toLowerCase())
      )
      return {
        ...group,
        options,
      }
    })
    .filter(group => group.options && group.options.length > 0)
}

export function optionGroupsForCurrentSearchState(
  filteredOptionGroups: FilterGroup[],
  unfilteredOptionGroups: FilterGroup[],
  context: {
    searchTerm: string
    isSearchable: boolean
  }
): FilterGroup[] {
  if (!context.isSearchable) {
    return unfilteredOptionGroups
  }

  if (context.searchTerm.trim() !== '' && filteredOptionGroups.length === 0) {
    return []
  }

  if (filteredOptionGroups.length > 0) {
    return filteredOptionGroups
  }

  return unfilteredOptionGroups
}

export function getSortedOptionGroups(
  filteredOptionGroups: FilterGroup[],
  unfilteredOptionGroups: FilterGroup[],
  context: {
    searchTerm: string
    isSearchable: boolean
  }
): FilterGroup[] {
  // Server load doesn't run the useEffect so we need to use the unfiltered option groups
  const optionGroupsUnsorted = optionGroupsForCurrentSearchState(
    filteredOptionGroups,
    unfilteredOptionGroups,
    context
  )

  if (context.isSearchable) {
    return sortBy(
      optionGroupsUnsorted.map(group => ({
        ...group,
        options: sortBy(group.options, [o => o.label.toLowerCase()]),
      })),
      [o => o.label?.toLowerCase()]
    )
  }

  return optionGroupsUnsorted
}

interface ReducerState {
  searchTerm: string
  filteredOptionGroups: FilterGroup[]
  lastUpdated: number
}

type ReducerAction =
  | { type: 'setSearchTerm'; searchTerm: string }
  | {
      type: 'setFilteredOptionGroups'
      filteredOptionGroups: FilterGroup[]
      timestamp: number
    }

const reduce = (state: ReducerState, action: ReducerAction): ReducerState => {
  switch (action.type) {
    case 'setSearchTerm': {
      return {
        ...state,
        searchTerm: action.searchTerm,
      }
    }
    case 'setFilteredOptionGroups': {
      if (state.lastUpdated < action.timestamp) {
        return {
          ...state,
          filteredOptionGroups: action.filteredOptionGroups,
          lastUpdated: action.timestamp,
        }
      } else {
        return state
      }
    }
    default: {
      return state
    }
  }
}

const DEBEOUNCED_SEARCH_DELAY = 100
export const _filterOptions = (
  id: string,
  unfilteredOptionGroups: FilterGroup[],
  searchTerm: string,
  dispatch: React.Dispatch<ReducerAction>,
  onSearch: OnSearchFunc
): void => {
  const filterOptions = async (): Promise<void> => {
    const timestamp = Date.now()
    const filteredOptionGroups =
      searchTerm === ''
        ? unfilteredOptionGroups
        : await onSearch(id, unfilteredOptionGroups, searchTerm)
    dispatch({
      type: 'setFilteredOptionGroups',
      filteredOptionGroups,
      timestamp,
    })
  }
  filterOptions()
}
const _debouncedFilterOptions = debounce(
  _filterOptions,
  DEBEOUNCED_SEARCH_DELAY,
  { leading: true }
)

const onChangeWithSelectedOption =
  (previousSelection: string[], onChange: OnChangeFunc) =>
  (selectedOptions: string[]): void => {
    const newCheckedSelection = selectedOptions.filter(
      option => !previousSelection.includes(option)
    )[0]
    const newUncheckedSelection = previousSelection.filter(
      option => !selectedOptions.includes(option)
    )[0]
    const changedOption = newCheckedSelection || newUncheckedSelection
    onChange(selectedOptions, {
      option: changedOption,
      isChecked: Boolean(newCheckedSelection),
    })
  }

export const SearchableFacetedFilter: React.FC<
  React.PropsWithChildren<SearchableFacetedFilterProps>
> = ({
  id,
  title,
  searchFieldPlaceholder = `Search ${title}`,
  optionGroups: unfilteredOptionGroups = [],
  isSearchable = false,
  isLinked = false,
  selectedOptions = [],
  onSearch = _filterOptonsBySearchTerm,
  onChange,
}) => {
  const [state, dispatch] = useReducer(reduce, {
    searchTerm: '',
    filteredOptionGroups: [],
    lastUpdated: 0,
  })

  useDeepCompareEffect(() => {
    _debouncedFilterOptions(
      id,
      unfilteredOptionGroups,
      state.searchTerm,
      dispatch,
      onSearch
    )
    return () => _debouncedFilterOptions.cancel()
  }, [id, state.searchTerm, onSearch, unfilteredOptionGroups])

  const optionGroups = getSortedOptionGroups(
    state.filteredOptionGroups,
    unfilteredOptionGroups,
    { isSearchable, searchTerm: state.searchTerm }
  )

  const onSelectionChange = onChangeWithSelectedOption(
    selectedOptions,
    onChange
  )

  return (
    <Stack gap="spacing-md">
      {isSearchable && (
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchMediumIcon />
          </InputLeftElement>
          <Input
            type="search"
            placeholder={searchFieldPlaceholder}
            value={state.searchTerm}
            onChange={e =>
              dispatch({ type: 'setSearchTerm', searchTerm: e.target.value })
            }
          />
        </InputGroup>
      )}
      {optionGroups && (
        <CheckboxGroup value={selectedOptions} onChange={onSelectionChange}>
          <Stack gap="spacing-md">
            {optionGroups.map(({ label: groupLabel, options }) => (
              <Stack key={groupLabel} gap="spacing-md">
                {groupLabel && <Text tone="strong">{groupLabel}</Text>}
                {options && (
                  <>
                    {groupLabel ? (
                      <Grid templateColumns="1fr 1fr" rowGap="spacing-sm">
                        {options.map(({ label: optionLabel, value }) => (
                          <GridItem key={value}>
                            <Checkbox value={value}>
                              {optionLabel.replace(/\//g, '/\u{200b}')}
                            </Checkbox>
                          </GridItem>
                        ))}
                      </Grid>
                    ) : (
                      <Stack gap="spacing-sm">
                        {options.map(({ label: optionLabel, value }) =>
                          isLinked ? (
                            <CheckboxWithLink
                              key={value}
                              name={id}
                              value={value}
                              onLinkClick={e => {
                                e.preventDefault()
                                onSelectionChange(
                                  selectedOptions.includes(value)
                                    ? selectedOptions.filter(x => x !== value)
                                    : [...selectedOptions, value]
                                )
                              }}
                            >
                              {optionLabel}
                            </CheckboxWithLink>
                          ) : (
                            <Checkbox key={value} value={value}>
                              {optionLabel}
                            </Checkbox>
                          )
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            ))}
          </Stack>
        </CheckboxGroup>
      )}
    </Stack>
  )
}
